/**
 * Libellés d'affichage des permissions (écran Rôles). Les codes restent ceux de
 * sys_permissions — seuls les titres et libellés affichés changent.
 * Module inconnu → code brut ; action inconnue → description de la permission.
 */
import { Permission } from '../models/models';
import { labels } from './labels';

const MODULE_LABEL: Record<string, string> = {
  articles:        'Articles',
  prixDepartements: 'Prix par département',
  tiers:           labels.tiersPluriel,
  biens:           labels.bienPluriel,
  contrats:        labels.contratPluriel,
  reglements:      labels.reglementPluriel,
  comptes:         'Comptes',
  stock:           'Stock',
  familles:        'Familles',
  departements:    'Départements',
  taches:          'Tâches',
  users:           'Utilisateurs',
  roles:           'Rôles & droits utilisateurs',
  docVentes:       labels.documentVentePluriel,
  notifications:   'Notifications',
  immobilisations: 'Immobilisations',
  options:         'Options personnalisables',
  aiToken:         'Tokens IA (MCP)',
  assistant:       'Assistant IA',
};

/** Explication affichée sous le titre — modules sensibles uniquement. */
const MODULE_AIDE: Record<string, string> = {
  roles:
    "« Gérer les droits » = attribuer des rôles, des permissions et des restrictions aux utilisateurs. " +
    "À réserver aux administrateurs : ce droit permet de se donner n'importe quel accès.",
  users:
    "Comptes de connexion. Choisir le rôle d'un utilisateur demande en plus " +
    "« Rôles & droits utilisateurs › Gérer les droits ».",
  prixDepartements:
    "Prix propres à chaque département (onglet de la fiche article, actions de masse). " +
    "Le prix du département en cours reste visible à la vente.",
  docVentes:
    "Deux niveaux. « Global » : actions qui ne visent pas un seul type (listes, transformation, " +
    "duplication, remise, prix, vente sous le coût). Par type : créer, modifier, supprimer, " +
    "clôturer un document de ce type et saisir ses lignes. Le global ne remplace jamais le " +
    "droit du type : transformer un document demande « Global › Créer » ET « Créer » du type produit.",
};

const ACTION_LABEL: Record<string, string> = {
  read:   'Consulter',
  create: 'Créer',
  update: 'Modifier',
  delete: 'Supprimer',
};

/** Libellés propres à un code précis (prioritaires sur l'action générique). */
const CODE_LABEL: Record<string, string> = {
  'roles.update': 'Gérer les droits',
  'prixDepartements.update': 'Créer / modifier',
  'articles.prixRevient':    'Voir le prix de revient',
};

export const permissionModuleLabel = (module: string): string => MODULE_LABEL[module] ?? module;

export const permissionModuleAide = (module: string): string | null => MODULE_AIDE[module] ?? null;

export const permissionActionLabel = (p: Permission): string => {
  if (CODE_LABEL[p.code]) return CODE_LABEL[p.code];
  const action = p.code.split('.').pop() ?? p.code;
  return ACTION_LABEL[action] ?? p.description ?? action;
};
